import type { BraceletPatternDto, CellValue } from '@miyuki/types';

import {
  assertColorIndex,
  withCell,
  NO_CHANGES,
  type CellPatch,
  type PatternEdit,
} from './pattern.entity';
import type { PatternTool } from './pattern.session';

/** Una posición de la cuadrícula. No valida nada por sí sola. */
export interface CellRef {
  readonly row: number;
  readonly column: number;
}

/**
 * Aplica muchas celdas de una vez: es lo que produce un trazo del dedo.
 *
 * Se diferencia de `paintCell` en algo deliberado: **descarta las celdas fuera
 * de la grilla en vez de fallar**. Un trazo que sale del diseño y vuelve a
 * entrar es un gesto corriente, y quien produce esas posiciones es la
 * aritmética de la geometría, no la usuaria. En `paintCell` un índice fuera de
 * rango sigue siendo un error de quien llama.
 *
 * Una celda que ya tiene el valor pedido no entra en el parche. Gracias a eso,
 * pasar dos veces por la misma celda durante un trazo la deja igual que pasar
 * una sola vez, y el parche nunca guarda dos veces la misma posición: por eso
 * los parches de un trazo se pueden concatenar sin cuidar el orden.
 */
export function paintCells(
  pattern: BraceletPatternDto,
  cells: readonly CellRef[],
  tool: PatternTool,
): PatternEdit {
  // El color se comprueba una vez, no por celda: es el mismo para todo el trazo.
  if (tool.kind === 'color') assertColorIndex(pattern, tool.index);

  const value: CellValue = tool.kind === 'eraser' ? null : tool.index;

  const inverse: CellPatch[] = [];
  const next = cells.reduce((current, { row, column }) => {
    if (!Number.isInteger(row) || row < 0 || row >= current.rows.length) return current;
    if (!Number.isInteger(column) || column < 0 || column >= current.columns) return current;

    const previous = current.rows[row]?.[column] ?? null;
    if (previous === value) return current;

    inverse.push({ row, column, value: previous });
    return withCell(current, row, column, value);
  }, pattern);

  if (inverse.length === 0) return { pattern, undo: NO_CHANGES };

  return { pattern: next, undo: { kind: 'cells', cells: inverse } };
}
